import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  styled,
} from '@mui/material';
import { darken } from '@mui/system';
import { useState } from 'react';
import { useIsoLanguageCode } from '../../../utils/hooks/useIsoLanguageCode';
import { getConsultationJotFormUrl } from '../utils/getConsultationJotFormUrl';

const RequestForConsultationBanner = () => {
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const isoLanguageCode = useIsoLanguageCode();

  const handleConsultationButtonClick = () => {
    setIsConsultationModalOpen(true);
    setIsIframeLoading(true);
  };

  const handleIframeLoad = () => {
    setIsIframeLoading(false);
  };

  return (
    <>
      <Modal
        open={isConsultationModalOpen}
        onClose={() => setIsConsultationModalOpen(false)}
        aria-labelledby="consultation-modal-title"
        aria-describedby="consultation-modal-description"
      >
        <StyledModalBox>
          <CloseIconButton
            aria-label="Close consultation modal"
            data-testid="consultation-modal-close"
            onClick={() => setIsConsultationModalOpen(false)}
          >
            <FontAwesomeIcon icon={faXmark} size="xs" />
          </CloseIconButton>
          {isIframeLoading && (
            <SpinnerContainer>
              <CircularProgress />
            </SpinnerContainer>
          )}
          <StyledIframe
            data-testid="consultation-jot-form"
            src={getConsultationJotFormUrl(isoLanguageCode)}
            title="Request a Consultation"
            onLoad={handleIframeLoad}
          />
        </StyledModalBox>
      </Modal>
      <StyledContainer data-testid="services-banner">
        <StyledBox>
          <StyledHeading>Cowbell Resiliency Services</StyledHeading>
          <StyledSubHeading>
            Cowbell Resiliency Services (CRS) equips policyholders with the
            tools and expertise to proactively detect and mitigate
            vulnerabilities, fortify infrastructure, and counter evolving
            threats.
          </StyledSubHeading>
          <StyledCtaButton
            role="button"
            aria-label="Request a consultation"
            onClick={handleConsultationButtonClick}
          >
            REQUEST A CONSULTATION
          </StyledCtaButton>
        </StyledBox>
      </StyledContainer>
    </>
  );
};

const StyledContainer = styled('div')(() => ({
  background: `url(images/resilience-request-a-consultation-banner-bg.png) no-repeat center right / cover`,
  width: '100%',
  borderRadius: '8px',
}));

const StyledBox = styled('div')(({ theme }) => ({
  width: '70%',
  padding: '3rem',

  [theme.breakpoints.up('md')]: {
    width: '50%',
  },
}));

const StyledHeading = styled('h1')(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  color: `${theme.palette.common.cowbellBlue} !important`,
  margin: '0.5rem 0',
}));

const StyledSubHeading = styled('h2')(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 500,
  color: theme.palette.common.darkGrey6,
  margin: '1rem 0',
  marginBottom: '3rem',
}));

const StyledCtaButton = styled('button')(({ theme }) => {
  const backgroundColor = theme.palette.primary.cowbellBlue;
  return {
    backgroundColor,
    color: theme.palette.common.white,
    padding: '1rem 1.5rem',
    fontSize: '1.25rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
    userSelect: 'none',

    '&:hover': {
      backgroundColor: darken(backgroundColor, 0.2),
    },
  };
});

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '50rem',
  background: theme.palette.common.white,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const StyledIframe = styled('iframe')(() => ({
  width: '100%',
  height: '90vh',
  border: 'none',
  display: 'block',

  '@media (max-width: 768px)': {
    height: '80vh',
  },
}));

const SpinnerContainer = styled('div')(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1,
}));

const CloseIconButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '1rem',
  right: '1rem',
  zIndex: 2,
  background: theme.palette.background.paper,
  borderRadius: '50%',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  '&:hover': {
    background: theme.palette.background.default,
  },
  padding: 4,
  color: theme.palette.common.darkGrey6,
  transition: 'background 0.2s, color 0.2s',
}));

export default RequestForConsultationBanner;
